import { withoutTrailingSlash } from 'ufo';
import { SITE_IDS } from '@pkg/config/sanity/audience';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { singletonDocIdByName } from '@pkg/sanity-utils/singletonDocId';
import { SINGLETON_DOCUMENT } from '@pkg/config/sanity/singletons';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import {
  authorsPathSegment,
  partnersCityPathSegment,
  partnersPathSegment,
  surpriseBagSeoKeyword,
} from '../../../sanity/schemas/utils/slugs/slugConstants';
import { indexPage } from '~/apis/sanity/queries/indexPage';
import { useBrandProfileData } from '~/app/composables/useBrandProfileData';
/* import { useJobPostData } from '~/app/composables/useJobPostData'; */
import { useSurpriseBagData } from '~/app/composables/useSurpriseBagData';
import { useAuthorData } from '~/app/composables/useAuthorData';
import { pageBySlug } from '~/apis/sanity/queries/pageBySlug';
import { postBySlug } from '~/apis/sanity/queries/postBySlug';
import { pathPartsToSlugParts, urlToPathParts } from '~/app/helpers/sanity/urlToSlugParts';
import { useSiteManifestStore } from '~/app/stores/siteManifest';

/**
 * For a particular slug, try and get the relevant page in Sanity.
 */
export function useSanityPageQuery<Type = unknown, Err = Error>() {
  const route = useRoute();
  const { locale } = useI18n();
  const siteManifest = useSiteManifestStore();
  const { public: { siteId } } = useRuntimeConfig();

  const localePath = useLocalePath();
  const unrefLocale = unref(locale) as LOCALE_CODE;

  const homePath = withoutTrailingSlash(localePath('/'));
  if (withoutTrailingSlash(route.path) === homePath) {
    const indexSiteId = siteId === SITE_IDS.BUSINESS ? SITE_IDS.BUSINESS : SITE_IDS.WWW;
    return useSanityData<Type, Err>(indexPage(unrefLocale, indexSiteId), { locale: unrefLocale });
  }

  const pathParts = urlToPathParts(route.path);
  const slugParts = pathPartsToSlugParts(pathParts);

  // If is author page - I.e /author/john-doe
  if (pathParts[0] === authorsPathSegment && pathParts.length === 2) {
    return useAuthorData({ slug: pathParts[1], locale: unrefLocale });
  }

  // If is blog article - I.e /blog/article-slug
  if (pathParts[0] === siteManifest?.slugs?.blog && pathParts.length > 1) {
    return useSanityData<Type, Err>(postBySlug, {
      type: DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
      defaultPageBlocksDocId: singletonDocIdByName(SINGLETON_DOCUMENT.BLOG_SINGLE_DEFAULT_BLOCKS, unrefLocale),
      locale: unrefLocale,
      ...slugParts,
    });
  }

  // If is press release - I.e /press/article-slug
  if (pathParts[0] === siteManifest?.slugs?.pressReleases && pathParts.length > 1) {
    return useSanityData<Type, Err>(postBySlug, {
      type: DOCUMENT.PRESS_RELEASES,
      defaultPageBlocksDocId: singletonDocIdByName(SINGLETON_DOCUMENT.PRESS_RELEASE_DEFAULT_BLOCKS, unrefLocale),
      locale: unrefLocale,
      ...slugParts,
    });
  }

  // Surprise Bag routes
  if (pathParts[0] === surpriseBagSeoKeyword && pathParts.length > 2) {
    return useSurpriseBagData({ locale: unrefLocale, type: DOCUMENT.FRAGMENT_SURPRISE_BAG, rawId: pathParts[pathParts.length - 1] });
  }

  // Career posts - OLD careers flow, replaced by Ashby iframe (jobs.ashbyhq.com/toogoodtogo).
  // Commented out until the old flow is fully removed in a later phase.
  /* if (pathParts[0] === siteManifest?.slugs?.careers && pathParts.length > 1 && pathParts[1].match('[0-9]+')) {
    return useJobPostData(pageBySlug(slugParts), { locale: unrefLocale, ...slugParts });
  } */

  // Brand profiles — /{locale}/partners/{slug} (hub) and /{locale}/partners-city/{city}/{slug} (city)
  const isPartnerPage = pathParts[0] === partnersPathSegment && pathParts.length === 2;
  const isCityRoute = pathParts[0] === partnersCityPathSegment && pathParts.length === 3;

  if (isPartnerPage || isCityRoute) {
    return useBrandProfileData(pageBySlug(slugParts), { locale: unrefLocale, ...slugParts });
  }

  // Fallback query for all other pages and the root of prefixed urls. I.e. /blog
  return useSanityData<Type, Err>(pageBySlug(slugParts), { locale: unrefLocale, ...slugParts });
}
